import { Button } from "@client/components/utils/Button";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { Tooltip } from "@client/components/utils/Tooltip";
import {
  accessiblePhases,
  allPhases,
  getRoundStatusDisplayName,
} from "@client/utils/roundStatusUtils";
import userIsAdmin from "@client/utils/userIsAdmin";
import type { TrackStatus } from "@engine/round/aggregate";
import { trackNames } from "@lib/types";
import clsx from "clsx";
import { useContext, useEffect } from "react";
import { useMutative } from "use-mutative";
import { Redirect } from "wouter";
import { DispatchContext, StateContext } from "../round/Round";

type State = {
  evaluationsDueDate: string | undefined;
  trackStatus: TrackStatus;
  hasUnsavedChanges: boolean;
};

const TrackSettings = withErrorBoundary(
  ({ track }: { track: "MAIN" | "FAIRNESS" | "FREEDOM" }) => {
    const dispatch = useContext(DispatchContext);
    const context = useContext(StateContext);
    const { round, actingAsUserId: userId } = context;
    const trackSettings = round.trackSettings[track];
    const [state, setState] = useMutative<State>({
      evaluationsDueDate: trackSettings.evaluationsDueDate,
      trackStatus: trackSettings.status,
      hasUnsavedChanges: false,
    });

    useEffect(() => {
      setState((draft) => {
        draft.trackStatus = trackSettings.status;
        draft.evaluationsDueDate = trackSettings.evaluationsDueDate;
        draft.hasUnsavedChanges = false;
      });
    }, [trackSettings, setState]);

    useEffect(() => {
      const handler = (e: BeforeUnloadEvent) => {
        if (state.hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = true;
        }
      };
      window.addEventListener("beforeunload", handler);

      return () => window.removeEventListener("beforeunload", handler);
    }, [state.hasUnsavedChanges]);

    const isAdmin = userIsAdmin(userId, context);
    if (!isAdmin) {
      return <Redirect to="/" />;
    }

    return (
      <div className="w-full h-full flex flex-col overflow-hidden">
        <h1 className="text-2xl font-bold p-4 pb-2">
          {trackNames[track]} Settings
        </h1>
        <div className="flex flex-col gap-5 overflow-y-auto p-4 pt-0">
          <div>
            <h2 className="text-lg font-bold pb-2">Evaluations Due Date</h2>
            <input
              id="evaluationsDueDate"
              type="date"
              value={state.evaluationsDueDate ?? ""}
              onChange={(e) => {
                setState((draft) => {
                  draft.evaluationsDueDate = e.target.value;
                  draft.hasUnsavedChanges = true;
                });
              }}
            />
          </div>

          <div>
            <h2 className="text-lg font-bold pb-2">Track Status</h2>
            <TrackStatusSelect
              trackStatus={state.trackStatus}
              setTrackStatus={(trackStatus) => {
                setState((draft) => {
                  draft.trackStatus = trackStatus;
                  draft.hasUnsavedChanges = true;
                });
              }}
            />
          </div>

          <div>
            <Button
              classes="min-w-48"
              onClick={async () => {
                await dispatch({
                  type: "SET_TRACK_SETTINGS",
                  track,
                  evaluationsDueDate: state.evaluationsDueDate,
                  status: state.trackStatus,
                });
                setState((draft) => {
                  draft.hasUnsavedChanges = false;
                });
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

function TrackStatusSelect({
  trackStatus,
  setTrackStatus,
}: {
  trackStatus: TrackStatus;
  setTrackStatus: (trackStatus: TrackStatus) => void;
}) {
  const navItemLabels = [
    "Onboarding",
    "Recusals",
    "Investigations",
    "My Evaluations",
    "Others' Evaluations",
    "Analysis",
  ] as const;
  const body = navItemLabels.map((label) => {
    const phaseSet = new Set(accessiblePhases(label, false));
    const phaseSetFunder = new Set(accessiblePhases(label, true));

    return (
      <tr key={label} className="even:bg-stone-50 dark:even:bg-stone-800">
        {allPhases.map((phase) => {
          const funderOnly = !phaseSet.has(phase) && phaseSetFunder.has(phase);
          return (
            <td
              key={phase}
              className={clsx(
                "p-2 border-r border-stone-300 dark:border-stone-700",
                funderOnly && "text-gray-400",
              )}
            >
              <Tooltip title={funderOnly ? "Funder only" : undefined}>
                {(phaseSet.has(phase) || phaseSetFunder.has(phase)) && label}
                {funderOnly && "*"}
              </Tooltip>
            </td>
          );
        })}
      </tr>
    );
  });

  return (
    <table className="border-collapse border border-stone-300 dark:border-stone-700 rounded-md overflow-hidden text-sm w-4xl text-gray-600 font-medium shadow-md">
      <thead className="bg-stone-200 dark:bg-stone-700">
        <tr>
          {allPhases.map((phase) => (
            <th
              key={phase}
              className="p-2 border-r border-stone-300 dark:border-stone-700"
            >
              <div className="flex flex-col items-center gap-1">
                <label htmlFor={phase} className="whitespace-nowrap w-full">
                  {getRoundStatusDisplayName(phase)}
                </label>
                <input
                  className="w-full"
                  id={phase}
                  type="radio"
                  checked={trackStatus === phase}
                  onChange={() => setTrackStatus(phase)}
                />
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{body}</tbody>
    </table>
  );
}

export default TrackSettings;
